import { Navigate, Route } from 'react-router-dom';
import { CatchupPlayer } from '../components/ui/CatchupPlayer';
import h from '../helpers/common';
import { isGamificationEnabled } from '../features/gamification/utils/GamificationUtils';
import { AddProfileScreen } from '../screen/AddProfilePage';
import { ColdStartScreen } from '../screen/ColdStartPage';
import { ConsentManagementScreen } from '../screen/ConsentManagementScreen';
import { EditProfileScreen } from '../screen/EditProfilePage';
import { EPGTvScreen } from '../screen/EpgTvScreen';
import NewScreen from '../screen/NewScreen';
import { NotFoundScreen } from '../screen/NotFoundScreen';
import { ParentalPinManagementScreen } from '../screen/ParentalPinManagementScreen';
import { PlayScreen } from '../screen/PlayScreen';
import { ProfileHistoryScreen } from '../screen/ProfileHistoryScreen';
import { UserProfileScreen } from '../screen/ProfileSettingsPage';
import ProvidersScreen from '../screen/ProvidersScreen';
import RedeemScreen from '../screen/RedeemScreen';
import RewardScreen from '../screen/RewardScreen';
import SearchScreen from '../screen/SearchScreen';
import { SelectProfileScreen } from '../screen/SelectProfilePage';
import { TvodsScreen } from '../screen/TvodsScreen';
import VerticalScreen from '../screen/VerticalScreen';
import { VodListScreen } from '../screen/VodListScreen';
import BlankLayout from '../layout/BlankLayout';
import MenuLayout from '../layout/MenuLayout';
import {
    loggedInUserRouteConditionMet,
    mainAppRouteConditionMet,
    userHasCatalogAccess,
} from '../config/guestMode';
import { ExitKidPage } from '../screen/ExitKidPage';

export const createLanguageRoutes = (context: any) => {
    const appConfig = h.getAppConfig();
    const gamificationConfig = isGamificationEnabled();
    const language = h.getLang();
    const mainAppConditions = [
        {
            condition: mainAppRouteConditionMet(context?.user, context?.selectedProfile),
            redirectTo: '/select-profile',
        },
        {
            condition: !context.maxDeviceReached,
            redirectTo: `/profile-settings?maxDeviceReached=${context.maxDeviceReached}`,
        },
    ];

    const loggedInUserConditions = [
        {
            condition: loggedInUserRouteConditionMet(context?.user, context?.profiles?.profiles?.length ?? 0),
            redirectTo: '/add-profile',
        },
    ];

    const isKids = context?.selectedProfile?.kidsMode;
    return (
        <Route path={`/:lang?`}>
            <Route element={<BlankLayout conditions={[]} context={context} />}>
                <Route
                    path="select-profile"
                    element={<SelectProfileScreen screenName="select-profile" screenKey="select-profile" />}
                />
                <Route
                    path="add-profile"
                    element={<AddProfileScreen screenName="add-profile" screenKey="add-profile" />}
                />

                <Route
                    path="edit-profile"
                    element={<EditProfileScreen screenName="edit-profile" screenKey="edit-profile" />}
                />
                <Route
                    path="parental-pin-management"
                    element={
                        <ParentalPinManagementScreen
                            screenName="parental-pin-management"
                            screenKey="parental-pin-management"
                        />
                    }
                />
            </Route>
            <Route
                element={
                    <BlankLayout conditions={[...loggedInUserConditions, ...mainAppConditions]} context={context} />
                }
            >
                <Route path="cold-start" element={<ColdStartScreen screenName="cold-start" screenKey="cold-start" />} />
                <Route path="catchup-player" element={<CatchupPlayer />} />
                <Route path="play" index element={<PlayScreen screenName="play" screenKey="play" />} />
                <Route
                    path="consent-management"
                    index
                    element={<ConsentManagementScreen screenName="consent-management" screenKey="consent-management" />}
                />
                <Route path="exit-kid" element={<ExitKidPage />} />
            </Route>
            <Route element={<MenuLayout conditions={[]} context={context} />}>
                <Route
                    path="profile-settings"
                    element={<UserProfileScreen screenName="profile-settings" screenKey="profile-settings" />}
                />
            </Route>
            <Route
                element={
                    <MenuLayout conditions={[...loggedInUserConditions, ...mainAppConditions]} context={context} />
                }
            >
                {/* only default profile access these routes */}
                {userHasCatalogAccess(context?.user) && !isKids ? (
                    <>
                        <Route path="movies" index element={<NewScreen screenName="movies" screenKey="movies" />} />
                        <Route
                            path="newandhot"
                            index
                            element={
                                <VerticalScreen
                                    screenName="newandupcoming"
                                    screenKey="newandupcoming"
                                    pathValue="newandhot"
                                />
                            }
                        />
                        <Route
                            path="profile-settings"
                            element={<UserProfileScreen screenName="profile-settings" screenKey="profile-settings" />}
                        />
                        <Route path="vod-list" element={<VodListScreen screenName="vod-list" screenKey="vod-list" />} />

                        <Route
                            path="profile-history"
                            element={<ProfileHistoryScreen screenName="profile-history" screenKey="profile-history" />}
                        />
                        <Route
                            path="providers"
                            index
                            element={<ProvidersScreen screenName="branded" screenKey="branded" />}
                        />
                        <Route path="shows" index element={<NewScreen screenName="tvshows" screenKey="tvshows" />} />
                        <Route path="tvods" index element={<TvodsScreen screenName="tvod" screenKey="tvod" />} />
                        <Route path="rewind" index element={<NewScreen screenName="rewind" screenKey="rewind" />} />
                        <Route path="sports" element={<NewScreen screenName="sports" screenKey="sports" />} />
                        <Route path="discover" element={<NewScreen screenName="discover" screenKey="discover" />} />
                        {gamificationConfig?.enable && <Route path="redeem" element={<RedeemScreen />} />}
                        {appConfig?.routes?.map((provider) => (
                            <Route
                                key={provider}
                                path={provider}
                                element={<NewScreen screenName={provider} screenKey={provider} />}
                            />
                        ))}
                        {appConfig?.mlp?.length > 0 &&
                            appConfig.mlp.map((mlpItem) => {
                                if (mlpItem.path && mlpItem.screenKey && mlpItem.screenName) {
                                    return (
                                        <Route
                                            key={mlpItem.id ?? mlpItem.path}
                                            path={mlpItem.path}
                                            element={
                                                <NewScreen
                                                    screenName={mlpItem.screenName}
                                                    screenKey={mlpItem.screenKey}
                                                    screenId={mlpItem.id}
                                                />
                                            }
                                        />
                                    );
                                }
                                return null;
                            })}
                    </>
                ) : null}
                {/* both kids and default profile access these routes */}
                {userHasCatalogAccess(context?.user) && (
                    <>
                        {isKids && language === 'en' && (
                            <Route index element={<Navigate to="/en/kids-home" replace={true} />} />
                        )}
                        {isKids && language === 'ar' && (
                            <Route index element={<Navigate to="/kids-home" replace={true} />} />
                        )}

                        <Route index element={<NewScreen screenName="home" screenKey="b2c_home" />} />
                        <Route index path="kids-home" element={<NewScreen screenName="kids" screenKey="kids" />} />
                        <Route path="livetv" element={<EPGTvScreen />} />
                        <Route path="watchlist" element={<NewScreen screenName="watchlist" screenKey="watchlist" />} />
                        <Route path="search" element={<SearchScreen screenName="wcpsearch" screenKey="wcpsearch" />} />

                        {gamificationConfig?.enable && (
                            <>
                                <Route path="rewards" element={<RewardScreen />} />
                            </>
                        )}
                    </>
                )}
            </Route>
            <Route path="*" element={<NotFoundScreen screenName="notfound" screenKey="notfound" />} />
        </Route>
    );
};
